import * as React from "react";

import "./card.css";

/**
 * Card, Tag and Divider — the pure-display surfaces, replacing `@salt-ds/core`'s for 469 Card
 * elements and 315 Tag elements.
 *
 * These three are grouped because they share a shape: they compute a class list and render one
 * div. There is no state, no floating layer, no keyboard handling — so the ONLY thing a port
 * can get wrong is the class list and the defaults feeding it, and both are pinned by tests.
 *
 * `InteractableCard` is deliberately NOT here. It carries group context, controlled selection,
 * radio/checkbox roles and a roving tabindex; it is an interactive component wearing a card's
 * name, and it belongs with Dialog and Menu rather than with these.
 */
export type CardVariant = "primary" | "secondary" | "tertiary" | "ghost";
export type CardAccent = "top" | "right" | "bottom" | "left";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  accent?: CardAccent;
  variant?: CardVariant;
  hoverable?: boolean;
  /** @deprecated the vendor points these at InteractableCard; kept because call sites pass them. */
  disabled?: boolean;
  interactable?: boolean;
}

function capitalize(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { accent, children, className, disabled, hoverable, interactable, variant = "primary", ...rest },
  ref,
) {
  const classes = ["saltCard", `saltCard-${variant}`];
  if (accent) {
    // BOTH classes, and they do different jobs: `saltCard-accent` turns the accent border on at
    // all, `saltCard-accentTop` picks the edge. Emitting only the second draws nothing.
    classes.push("saltCard-accent", `saltCard-accent${capitalize(accent)}`);
  }
  if (hoverable) classes.push("saltCard-hoverable");
  if (disabled) classes.push("saltCard-disabled");
  if (interactable) classes.push("saltCard-interactable");
  if (className) classes.push(className);

  return (
    <div className={classes.join(" ")} ref={ref} {...rest}>
      {children}
    </div>
  );
});

export type TagVariant = "primary" | "secondary";

export interface TagProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: TagVariant;
  /** The vendor's categorical colour slot, 1–20. Our tokens neutralise these deliberately. */
  category?: number;
  bordered?: boolean;
}

export const Tag = React.forwardRef<HTMLDivElement, TagProps>(function Tag(
  { bordered, category = 1, children, className, variant = "primary", ...rest },
  ref,
) {
  const classes = ["saltTag", `saltTag-category-${category}`, `saltTag-${variant}`];
  if (bordered) classes.push("saltTag-bordered");
  if (className) classes.push(className);

  return (
    <div className={classes.join(" ")} ref={ref} {...rest}>
      {children}
    </div>
  );
});

export type DividerOrientation = "horizontal" | "vertical";
export type DividerVariant = "primary" | "secondary" | "tertiary";

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: DividerOrientation;
  variant?: DividerVariant;
}

export const Divider = React.forwardRef<HTMLDivElement, DividerProps>(function Divider(
  { className, orientation = "horizontal", variant = "primary", ...rest },
  ref,
) {
  const classes = ["saltDivider", `saltDivider-${orientation}`, `saltDivider-${variant}`];
  if (className) classes.push(className);

  return (
    // `role="separator"` plus `aria-orientation` is what makes this a structural element to a
    // screen reader rather than an empty div. A styled <hr> would not carry the orientation.
    <div
      aria-orientation={orientation}
      className={classes.join(" ")}
      ref={ref}
      role="separator"
      {...rest}
    />
  );
});
